(() => {
  const ROOT = '[data-bw-suppliers]';
  const AUTO_SPEED = 0.045;
  const MAX_FLING_SPEED = 2.4;
  const FLING_DECAY_PER_FRAME = 0.965;
  const INTENT_DISTANCE = 7;
  const HORIZONTAL_BIAS = 1.12;
  const SAMPLE_WINDOW = 120;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  function init(root) {
    if (!root || root.dataset.bwSuppliersEnhanced === 'true') return;

    const viewport = root.querySelector('[data-bw-suppliers-viewport]');
    const sets = [...root.querySelectorAll('[data-bw-suppliers-set]')];
    if (!viewport || sets.length < 3) return;

    root.dataset.bwSuppliersEnhanced = 'true';
    root.dataset.bwSuppliersAutoplay = 'running';
    viewport.style.touchAction = 'pan-y';

    let setWidth = 0;
    let virtualScroll = 0;
    let raf = 0;
    let lastFrame = performance.now();
    let pointerId = null;
    let pointerType = '';
    let pointerDown = false;
    let dragging = false;
    let startX = 0;
    let startY = 0;
    let lastX = 0;
    let inertiaVelocity = 0;
    let samples = [];

    const commitScroll = () => {
      viewport.scrollLeft = virtualScroll;
    };

    const normalize = () => {
      if (!setWidth) return;
      while (virtualScroll < setWidth * 0.5) virtualScroll += setWidth;
      while (virtualScroll > setWidth * 1.5) virtualScroll -= setWidth;
      commitScroll();
    };

    const measure = () => {
      const previousWidth = setWidth;
      const previousScroll = virtualScroll || viewport.scrollLeft;
      setWidth = sets[1].getBoundingClientRect().width || sets[0].getBoundingClientRect().width;
      if (!setWidth) return;

      if (!previousWidth) {
        virtualScroll = setWidth;
        commitScroll();
        return;
      }

      if (Math.abs(previousWidth - setWidth) > 1) {
        const cycleProgress = (((previousScroll - previousWidth) % previousWidth) + previousWidth) % previousWidth;
        virtualScroll = setWidth + (cycleProgress / previousWidth) * setWidth;
      }
      normalize();
    };

    const addSample = (x, time) => {
      samples.push({ x, time });
      const cutoff = time - SAMPLE_WINDOW;
      while (samples.length > 2 && samples[0].time < cutoff) samples.shift();
    };

    const releaseVelocity = () => {
      if (samples.length < 2) return 0;
      const first = samples[0];
      const last = samples[samples.length - 1];
      const elapsed = Math.max(1, last.time - first.time);
      return clamp(-(last.x - first.x) / elapsed, -MAX_FLING_SPEED, MAX_FLING_SPEED);
    };

    const beginDrag = (event) => {
      dragging = true;
      inertiaVelocity = 0;
      virtualScroll = viewport.scrollLeft;
      viewport.classList.add('is-dragging');
      if (viewport.setPointerCapture) {
        try { viewport.setPointerCapture(event.pointerId); } catch (_) {}
      }
    };

    const onPointerDown = (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (pointerDown) return;

      pointerDown = true;
      pointerId = event.pointerId;
      pointerType = event.pointerType || 'touch';
      startX = event.clientX;
      startY = event.clientY;
      lastX = event.clientX;
      samples = [{ x: event.clientX, time: event.timeStamp || performance.now() }];
      inertiaVelocity = 0;

      if (pointerType === 'mouse') beginDrag(event);
    };

    const onPointerMove = (event) => {
      if (!pointerDown || event.pointerId !== pointerId) return;

      const totalX = event.clientX - startX;
      const totalY = event.clientY - startY;

      if (!dragging) {
        const absX = Math.abs(totalX);
        const absY = Math.abs(totalY);
        if (absX < INTENT_DISTANCE && absY < INTENT_DISTANCE) return;
        if (absX <= absY * HORIZONTAL_BIAS) return;
        beginDrag(event);
      }

      if (!dragging) return;
      event.preventDefault();

      const dx = event.clientX - lastX;
      lastX = event.clientX;
      virtualScroll -= dx;
      normalize();
      addSample(event.clientX, event.timeStamp || performance.now());
    };

    const finishPointer = (event) => {
      if (!pointerDown || event.pointerId !== pointerId) return;

      if (dragging) {
        addSample(event.clientX, event.timeStamp || performance.now());
        inertiaVelocity = releaseVelocity();
        viewport.classList.remove('is-dragging');
        normalize();
      }

      if (viewport.hasPointerCapture && viewport.hasPointerCapture(pointerId)) {
        try { viewport.releasePointerCapture(pointerId); } catch (_) {}
      }

      pointerDown = false;
      dragging = false;
      pointerId = null;
      pointerType = '';
      samples = [];
      lastFrame = performance.now();
    };

    const cancelPointer = (event) => {
      if (!pointerDown || event.pointerId !== pointerId) return;
      viewport.classList.remove('is-dragging');
      pointerDown = false;
      dragging = false;
      pointerId = null;
      pointerType = '';
      samples = [];
      inertiaVelocity = 0;
      virtualScroll = viewport.scrollLeft;
      lastFrame = performance.now();
    };

    const onWheel = (event) => {
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
        ? event.deltaX
        : (event.shiftKey ? event.deltaY : 0);
      if (!delta) return;
      event.preventDefault();
      inertiaVelocity = 0;
      virtualScroll += delta;
      normalize();
    };

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove, { passive: false });
    viewport.addEventListener('pointerup', finishPointer);
    viewport.addEventListener('pointercancel', cancelPointer);
    viewport.addEventListener('wheel', onWheel, { passive: false });

    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(measure) : null;
    if (resizeObserver) resizeObserver.observe(viewport);

    requestAnimationFrame(measure);

    const draw = (now) => {
      const dt = Math.min(34, Math.max(0, now - lastFrame));
      lastFrame = now;

      if (!dragging && setWidth) {
        virtualScroll += (AUTO_SPEED + inertiaVelocity) * dt;
        normalize();

        if (Math.abs(inertiaVelocity) > 0.004) {
          inertiaVelocity *= Math.pow(FLING_DECAY_PER_FRAME, dt / 16.667);
        } else {
          inertiaVelocity = 0;
        }
      }

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    root._bwSuppliersDestroy = () => {
      if (raf) cancelAnimationFrame(raf);
      if (resizeObserver) resizeObserver.disconnect();
      viewport.removeEventListener('pointerdown', onPointerDown);
      viewport.removeEventListener('pointermove', onPointerMove);
      viewport.removeEventListener('pointerup', finishPointer);
      viewport.removeEventListener('pointercancel', cancelPointer);
      viewport.removeEventListener('wheel', onWheel);
      viewport.classList.remove('is-dragging');
      delete root.dataset.bwSuppliersEnhanced;
      delete root.dataset.bwSuppliersAutoplay;
    };
  }

  const initAll = (scope = document) => scope.querySelectorAll(ROOT).forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches && event.target.matches(ROOT)
      ? event.target
      : event.target.querySelector && event.target.querySelector(ROOT);
    if (root && root._bwSuppliersDestroy) root._bwSuppliersDestroy();
  });
})();
